import React from "react";
import { Typography } from "@mui/material";

const TopEntryExit = ({ analyticsData, themeBg }) => {
  const allEntryPages = analyticsData?.pages?.topEntry || [];
  const allExitPages = analyticsData?.pages?.topExit || [];

  // Limit to showing only 6 entries per section
  const displayedEntryPages = allEntryPages.slice(0, 6);
  const displayedExitPages = allExitPages.slice(0, 6);

  // Calculate how many entries are not shown
  const remainingEntryCount = Math.max(0, allEntryPages.length - 6);
  const remainingExitCount = Math.max(0, allExitPages.length - 6);

  // Function to clean URL by removing the domain prefix
  const cleanUrl = (url) => {
    if (!url) return "";

    // If it's exactly the homepage URL or homepage with trailing slash, keep it as is
    if (
      url === "https://www.bapujidental.edu/" ||
      url === "https://www.bapujidental.edu"
    ) {
      return url;
    }

    // Otherwise remove the domain prefix for all other URLs
    return url.replace(/https?:\/\/www\.bapujidental\.edu(\/|$)/, "/");
  };

  return (
    <div
      style={{
        backgroundColor: themeBg || "#0F766E",
        borderRadius: "12px",
        padding: "20px",
        color: "white",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
      }}
    >
      {/* Top Entry Section */}
      <Typography
        variant="h6"
        style={{ fontWeight: "bold", marginBottom: "10px", borderBottom: "2px solid rgba(256, 256, 256, 0.2)", paddingBottom: "10px" }}
      >
        Top Entry Pages
      </Typography>

      {/* Table layout for better alignment - Entry Pages */}
      <table
        style={{
          width: "100%",
          borderCollapse: "separate",
          borderSpacing: "0 5px",
          marginTop: "10px",
          marginBottom: "10px", // Reduced bottom margin
        }}
      >
        {/* Table Header */}
        <thead>
          <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.3)" }}>
            <th
              style={{
                textAlign: "left",
                fontSize: "12px",
                fontWeight: "bold",
                paddingBottom: "8px",
                width: "75%",
              }}
            >
              Top Entry URLs
            </th>
            <th
              style={{
                textAlign: "right",
                fontSize: "12px",
                fontWeight: "bold",
                paddingBottom: "8px",
                width: "25%",
              }}
            >
              Sessions
            </th>
          </tr>
        </thead>

        {/* Table Body - Limited to top 6 */}
        <tbody>
          {displayedEntryPages.length > 0 ? (
            displayedEntryPages.map((page, index) => (
              <tr
                key={index}
                style={{
                  backgroundColor:
                    index % 2 === 0 ? "rgba(255,255,255,0.05)" : "transparent",
                }}
              >
                <td
                  style={{
                    padding: "6px 8px 6px 0",
                    fontFamily: "'Roboto Mono', monospace",
                    fontSize: "12px",
                    wordBreak: "break-all",
                    maxWidth: "0", // Forces text ellipsis to work with flex
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {cleanUrl(page.url)}
                </td>
                <td
                  style={{
                    padding: "6px 0",
                    textAlign: "right",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                >
                  {page.sessions}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={2}
                style={{ textAlign: "center", padding: "10px 0" }}
              >
                No entry page data available
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Note about additional entry pages */}
      {remainingEntryCount > 0 && (
        <div
          style={{
            textAlign: "center",
            fontSize: "12px",
            color: "rgba(255,255,255,0.7)",
            fontStyle: "italic",
            borderTop: "1px dashed rgba(255,255,255,0.3)",
            paddingTop: "6px",
            marginBottom: "20px",
          }}
        >
          + {remainingEntryCount} more entry page
          {remainingEntryCount !== 1 ? "s" : ""} not shown
        </div>
      )}

      {/* Top Exit Section */}
      <Typography
        variant="h6"
        style={{ fontWeight: "bold", marginBottom: "10px", borderBottom: "2px solid rgba(256, 256, 256, 0.2)", paddingBottom: "10px" }}
      >
        Top Exit Pages
      </Typography>

      {/* Table layout for better alignment - Exit Pages */}
      <table
        style={{
          width: "100%",
          borderCollapse: "separate",
          borderSpacing: "0 5px",
          marginTop: "10px",
        }}
      >
        {/* Table Header */}
        <thead>
          <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.3)" }}>
            <th
              style={{
                textAlign: "left",
                fontSize: "12px",
                fontWeight: "bold",
                paddingBottom: "8px",
                width: "75%",
              }}
            >
              Top Exit URLs
            </th>
            <th
              style={{
                textAlign: "right",
                fontSize: "12px",
                fontWeight: "bold",
                paddingBottom: "8px",
                width: "25%",
              }}
            >
              Sessions
            </th>
          </tr>
        </thead>

        {/* Table Body - Limited to top 6 */}
        <tbody>
          {displayedExitPages.length > 0 ? (
            displayedExitPages.map((page, index) => (
              <tr
                key={index}
                style={{
                  backgroundColor:
                    index % 2 === 0 ? "rgba(255,255,255,0.05)" : "transparent",
                }}
              >
                <td
                  style={{
                    padding: "6px 8px 6px 0",
                    fontFamily: "'Roboto Mono', monospace",
                    fontSize: "12px",
                    wordBreak: "break-all",
                    maxWidth: "0",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {cleanUrl(page.url)}
                </td>
                <td
                  style={{
                    padding: "6px 0",
                    textAlign: "right",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                >
                  {page.sessions}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={2}
                style={{ textAlign: "center", padding: "10px 0" }}
              >
                No exit page data available
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Note about additional exit pages */}
      {remainingExitCount > 0 && (
        <div
          style={{
            textAlign: "center",
            fontSize: "12px",
            color: "rgba(255,255,255,0.7)",
            fontStyle: "italic",
            borderTop: "1px dashed rgba(255,255,255,0.3)",
            paddingTop: "6px",
            marginTop: "10px",
          }}
        >
          + {remainingExitCount} more exit page
          {remainingExitCount !== 1 ? "s" : ""} not shown
        </div>
      )}
    </div>
  );
};

export default TopEntryExit;
